import React, { useState, useEffect } from 'react'
import styled, { css, keyframes } from 'styled-components'
import { IconClose, Typography, Button } from '../'
import { Modal as LibModal } from '@sentecacommerce-theme/lib'
import { Config } from '../../../config'
import { OverflowHidden } from '../OverflowHidden'

const { palette } = Config as any

const openAniDuration = 0.3
const closeAniDuration = 0.31

// TODO: make only the content inside the modal focusable: WAI-ARIA standards
export const Modal = ({
  open,
  position,
  maxWidth,
  width,
  title,
  titleStyle,
  subTitle,
  noContentPadding,
  children,
  handleClose,
  withBottomOffset,
  background = 'secondary',
  full = false,
  containerRef,
  smallPadding = false,
  noTitlePadding = false,
  hideHeader = false,
  bottomOnMobile = false,
  fullOnMobile = false,
  borderRadius = 'small',
  circleClose = false,
  isCookies,
  isCookiesDetails,
  isInFilters = false,
}: ModalProps) => {
  const [isClosing, setIsClosing] = useState(false)
  const [show, setShow] = useState(false)

  useEffect(() => {
    setTimeout(() => {
      setShow(true)
    }, 20)
  }, [open])

  return (
    <LibModal open={open} onClose={handleClose} zIndex={9999}>
      <ModalContainer
        open={open && !isClosing}
        position={position}
        withBottomOffset={withBottomOffset}
        background={palette[background]}
        full={full}
        bottomOnMobile={bottomOnMobile}
        fullOnMobile={fullOnMobile}
        isInFilters={isInFilters}
      >
        <OverflowHidden />

        <Background onClick={handleClose} />
        <ModalContentWrapper
          onClick={(e) => e.stopPropagation()}
          maxWidth={maxWidth}
          width={width}
          position={position}
          fullOnMobile={fullOnMobile}
          ref={containerRef}
          withBottomOffset={withBottomOffset}
          full={full}
          data-border-radius={position === 'edge' ? 'none' : borderRadius}
          bottomOnMobile={bottomOnMobile}
          isCookiesDetails={isCookiesDetails}
          open={show}
          isInFilters={isInFilters}
        >
          {!hideHeader && (
            <ModalTitleContainer isInFilters={isInFilters}>
              <ModalTitleWrapper full={full} noPadding={noTitlePadding}>
                {isCookies ? (
                  <></>
                ) : (
                  <CloseButtonContainer isInFilters={isInFilters}>
                    <Button
                      variant="light"
                      bordered
                      small
                      onClick={() => {
                        setIsClosing(true)
                        if (isInFilters) {
                          setTimeout(() => {
                            handleClose()
                          }, closeAniDuration * 1000)
                        } else {
                          handleClose()
                        }
                      }}
                      borderRadius="circle"
                      icon={<IconClose />}
                    ></Button>
                  </CloseButtonContainer>
                )}
                {title && (
                  <ModalTitle titleStyle={titleStyle}>{title}</ModalTitle>
                )}
                {subTitle && <ModalSubtitle>{subTitle}</ModalSubtitle>}
              </ModalTitleWrapper>
            </ModalTitleContainer>
          )}
          <ModalContent
            position={position}
            noPadding={noContentPadding}
            smallPadding={smallPadding}
            withBottomOffset={withBottomOffset}
          >
            {children}
          </ModalContent>
        </ModalContentWrapper>
      </ModalContainer>
    </LibModal>
  )
}

export type ModalProps = {
  open?: boolean
  position?: string
  maxWidth?: any
  width?: any
  title?: string
  titleStyle?: string
  subTitle?: string
  noContentPadding?: boolean
  children?: any
  handleClose?: any
  withBottomOffset?: boolean
  smallPadding?: boolean
  full?: boolean
  hideHeader?: boolean
  noTitlePadding?: boolean
  circleClose?: boolean
  bottomOnMobile?: boolean
  fullOnMobile?: boolean
  background?: 'primary' | 'secondary' | 'hint' | 'light'
  containerRef?: React.MutableRefObject<any>
  borderRadius?: 'small' | 'normal' | 'large' | 'circle' | 'none'
  isCookies?: boolean
  isCookiesDetails?: boolean
  isInFilters?: boolean
}

export type SetBodyOverflowProps = {
  open?: boolean
  hidden?: boolean
}

export type ModalContainerProps = {
  open?: boolean
  position?: string
  withBottomOffset?: boolean
  background?: string
  full?: boolean
  bottomOnMobile?: boolean
  fullOnMobile?: boolean
  isInFilters?: boolean
}

// For mobile filters on listing page
const openAni = keyframes`
  from { 
    transform: translateY(100vh);
  }

  to {
    transform: translateY(0); 
  }
`

const closeAni = keyframes`
   from { 
    transform: translateY(0);
  }

  to {
    transform: translateY(100vh); 
  }
`
//

const ModalContainer = styled.div<ModalContainerProps>`
  overflow: auto;
  display: flex;

  transition: all 0.3s ease;

  /* For mobile filters on listing page */
  ${({ open, isInFilters }) =>
    isInFilters &&
    css`
      ${open
        ? css`
            animation: ${openAni} ${openAniDuration}s
              cubic-bezier(0.215, 0.61, 0.355, 1) forwards;
          `
        : css`
            animation: ${closeAni} ${closeAniDuration}s
              cubic-bezier(0.55, 0.055, 0.675, 0.19) forwards;
          `}
    `}

  justify-content: center;
  align-items: ${(props) => (props.position === 'top' ? 'baseline' : 'center')};
  position: fixed;
  top: ${(props) => (props.position === 'top' ? '6rem' : 0)};
  left: 0;
  width: 100vw;
  height: ${(props) => (props.withBottomOffset ? '100%' : '100vh')};
  max-height: ${(props) => (props.withBottomOffset ? '100%' : '100vh')};
  z-index: 99999;
  box-sizing: inherit;

  @media (max-width: ${({ theme }) => theme.breakpoints.md.max}) {
    top: ${(props) => (props.position === 'top' ? '1rem' : 0)};
    background: ${(props) =>
      props.fullOnMobile || props.full
        ? props.background
        : 'rgba(34, 34, 34, 0.5)'};
    ${(props) => (props.bottomOnMobile ? 'align-items: flex-end;' : '')}
  }
  &::before {
    content: '';
    right: 0;
    bottom: 0;
    background: ${(props) =>
      props.full ? props.background : 'rgba(34, 34, 34, 0.5)'};
    position: fixed;
    top: 0;
    left: 0;
    height: ${(props) => (props.withBottomOffset ? '100%' : '100vh')};
  }
`

const CloseButtonContainer = styled.div<{ isInFilters?: boolean }>`
  position: absolute;
  top: 1.4rem;
  right: 1.2rem;

  padding: ${({ isInFilters }) => isInFilters && '0.8rem 2.4rem'};

  @media (min-width: ${({ theme }) => theme.breakpoints.lg.min}) {
    top: ${({ isInFilters }) => (isInFilters ? '2.4rem' : '2rem')};
    right: ${({ isInFilters }) => (isInFilters ? '1.2rem' : '2.6rem')};
  }

  & button {
    padding: 0;
    min-width: 3rem;
    min-height: 3rem !important;
    max-width: 3rem;
    max-height: 3rem;

    display: flex;
    align-items: center;
    justify-content: center;

    border-width: 0 !important;
    background: ${({ theme }) => theme.palette.secondary} !important;

    &:hover {
      background: ${({ theme }) => theme.palette.panache} !important;
    }
  }

  & span {
    position: static;
  }

  & svg {
    height: ${({ isInFilters }) => (isInFilters ? '1.7rem' : '1.2rem')};
  }
`

export type ModalContentWrapperProps = {
  position?: string
  maxWidth?: any
  width?: any
  withBottomOffset?: any
  open?: boolean
  full?: boolean
  noPadding?: boolean
  bottomOnMobile?: boolean
  fullOnMobile?: boolean
  isCookiesDetails?: boolean
  isInFilters?: boolean
}

const ModalContentWrapper = styled.div.attrs({
  className: 'p-0',
})<ModalContentWrapperProps>`
  max-height: ${(props) =>
    props.position === 'edge' ? '100vh' : 'min(80rem, calc(100vh - 2rem))'};

  ${(props) =>
    props.isCookiesDetails &&
    `
  min-height: 70rem;
  `}
  height: ${(props) => (props.position === 'edge' ? '100vh' : 'auto')};

  ${({ isInFilters, open }) =>
    !isInFilters &&
    `
    transition: all 0.3s ease;
    transform: translateY(${open ? '0' : '2rem'});
    opacity: ${open ? 1 : 0};
  `}

  max-width: ${(props) => (props.maxWidth ? props.maxWidth : '78.5rem')};
  width: ${(props) => (props.width ? props.width : 'calc(100% - 2rem)')};
  position: relative;
  min-width: 10rem;
  overflow: auto;
  box-shadow: 0 0 1.6rem 0.4rem rgba(0, 0, 0, 0.08);
  background: ${(props) =>
    props.withBottomOffset ? 'transparent' : props.theme.palette.secondary};

  @media (min-width: ${({ theme }) => theme.breakpoints.lg.min}) {
    width: ${(props) =>
      props.position === 'edge' || props.full
        ? '100vw'
        : 'calc(100% - 2.4rem)'};
    padding-bottom: 0;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md.max}) {
    ${(props) =>
      props.fullOnMobile &&
      css`
        max-width: none !important;
        max-height: none !important;
        width: 100vw !important;
        height: 100% !important;
        border-radius: 0 !important;
      `}
    ${(props) =>
      props.bottomOnMobile &&
      css`
        border-bottom-left-radius: 0 !important;
        border-bottom-right-radius: 0 !important;
      `}
  }

  ${(props) =>
    props.full &&
    css`
      max-height: none;
      height: 100vh;
      box-shadow: none;
      width: 100vw;
      max-width: none;
    `}
`

const Background = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  left: 0;
  top: 0;
`

const ModalTitleContainer = styled.div<{ isInFilters?: boolean }>`
  position: ${({ isInFilters }) => (isInFilters ? 'static' : 'sticky')};
  top: 0;
  left: 0;
  z-index: 3;
`

const ModalTitleWrapper = styled.div<ModalContentWrapperProps>`
  ${(props) =>
    props.full || props.noPadding ? '' : 'padding: 2.4rem 2.4rem 1.2rem'};
  background: ${({ theme }) => theme.palette.secondary};
  transition: box-shadow 0.5s;

  @media (min-width: ${({ theme }) => theme.breakpoints.lg.min}) {
    padding-left: 4.8rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md.max}) {
    padding: ${(props) =>
      props.noPadding ? 0 : '1.2rem 3.6rem 1.2rem 1.6rem'};
  }
`

export type ModalTitleProps = {
  titleStyle?: string
}

const ModalTitle = styled(Typography).attrs({
  as: 'h2',
})<ModalTitleProps>`
  font-weight: ${(props) => (props.titleStyle == 'normal' ? 'normal' : 'bold')};
  font-size: 2.2rem;
  line-height: 1.55;
`

const ModalSubtitle = styled(Typography).attrs({
  as: 'h3',
  variant: 'base',
})``

export type ModalContentProps = {
  noPadding?: boolean
  withBottomOffset?: boolean
  position?: string
  smallPadding?: boolean
}

const ModalContent = styled.div<ModalContentProps>`
  padding: ${(props) => (props.noPadding ? 0 : '2.4rem')};
  width: 100%;
  box-sizing: border-box;
  background: ${({ theme }) => theme.palette.secondary};
  @media (min-width: ${({ theme }) => theme.breakpoints.lg.min}) {
    padding: ${(props) => {
      if (props.noPadding) return 0
      if (props.smallPadding) return '2.4rem'
      return '2.4rem 4.8rem'
    }};
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.md.max}) {
    padding: ${(props) => {
      if (props.noPadding) return 0
      if (props.smallPadding) return '2.4rem'
      return '1.2rem'
    }};
  }
  ${(props) =>
    props.withBottomOffset &&
    css`
      height: 100%;
      padding-bottom: 6rem !important;
    `}
  ${(props) =>
    props.position === 'edge' &&
    css`
      @media (min-width: ${({ theme }) => theme.breakpoints.lg.min}) {
        transform: translateY(calc(50% - 100px));
      }
    `};
`
